import React, { useCallback } from 'react';
import { ReactComponent as AddIngredient } from 'Icons/Sidebar/AddIngredient.svg';
import { ReactComponent as Copy } from 'Icons/Sidebar/Copy.svg';
import { ReactComponent as Paste } from 'Icons/Sidebar/Paste.svg';
import { ReactComponent as GripIndicator } from 'Icons/Sidebar/GripIndicator.svg';
import { ReactComponent as AddCondition } from 'Icons/Sidebar/AddCondition.svg';
import { ReactComponent as DelCond } from 'Icons/Sidebar/DelCond.svg';
import '../../commandpanel/CustomLayer/CustomLayer.scss';
import { ConditionCardProps } from 'business/models/CustomModels';
import ELayersView from 'enums/ELayersView';
import { IngredientCard } from 'components/customComponents/Layers/InputGroups';
import 'components/customComponents/Layers/OptionsGroups.scss';

import { ConditionIngredientDTO, CustomLayerDTO, LayerDTO } from 'business/models/Models';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  updateExperimentLayers,
  preserveUndoableState,
} from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayer,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { useIsScreenIngredientEditable, useIsScreenInUse } from 'redux/hooks/common';

export default function ConditionCard({
  condition,
  setContentView,
  setEditIngredientData,
  setSelectedIngredient,
  delConditionDelegate,
  copyConditionDelegate,
  pasteConditionDelegate,
  buffer,
  insertConditionDelegate,
  swapConditionsDelegate,
  copyActive,
  refreshUI,
  isEditMode = true,
}: ConditionCardProps) {
  const isScreenEditable: boolean = useIsScreenIngredientEditable();
  const conditionNumberLable = 'Condition# ' + condition.conditionNumber;
  const isScreenInUse: boolean = useIsScreenInUse();
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const _layers = useRMSelector(selectExperimentLayers);
  const dispatch = useRMDispatch();
  const _setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = useCallback(
    () => dispatch(preserveUndoableState()),
    [dispatch],
  );

  function addCondition() {
    insertConditionDelegate(condition.conditionNumber);
  }

  function addIngredient() {
    setContentView(ELayersView.SELECTINGREDIENTSVIEW);
    setEditIngredientData(
      new ConditionIngredientDTO({
        conditionId: condition.id,
        ingredientIngredientTypeId: 0,
        ingredientStockId: 0,
        id: null,
        concentration: 0,
      }),
    );
  }
  function deleteConditionIngredient(ingId) {
    const layers = cloneDeep(_layers);
    const layerIndex = _layers.findIndex((lyr) => lyr.id === _selectedLayer.id);

    const deleteIndex = condition.conditionIngredient.findIndex(
      (ing) => ing.id === ingId,
    );

    (layers[layerIndex].layerDetail as CustomLayerDTO).condition.forEach((cond) => {
      if (cond.id == condition.id) {
        cond.conditionIngredient.splice(deleteIndex, 1);
      }
    });
    layers[layerIndex].dirty = true;
    callPreserveUndoableState();
    _setLayers(layers);
  }

  function handleCopy() {
    copyConditionDelegate(condition);
  }

  function handlePaste() {
    pasteConditionDelegate(condition);
  }

  function handleDelete() {
    delConditionDelegate(condition);
  }

  function onDragStart(e, id) {
    e.dataTransfer.setData('id', id);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  function onDrop(e, id) {
    const sourceCondNum = e.dataTransfer.getData('id');
    swapConditionsDelegate(sourceCondNum, id);
  }

  return (
    <div
      id='commandpanel-conditioncard-wrapper'
      className="flex flex-column w-100pr"
      style={{
        border: '1px #ccc solid',
        padding: '4px',
        marginTop: '8px',
        borderRadius: '4px',
      }}
    >
      <div id='commandpanel-conditioncard-container' className="flex flex-space-between " style={{ borderRadius: '4px' }}>
        <div
          id='commandpanel-conditioncard-editmode'
          className="flex flex-space-evenly flex-y-center"
          style={{ width: '45%', height: 50 }}
        >
          {isEditMode && (
            <div
              id='commandpanel-conditioncard-editmode-gripindicator'
              draggable
              onDragStart={(e) => onDragStart(e, condition.conditionNumber)}
              onDragOver={(e) => onDragOver(e)}
              onDrop={(e) => onDrop(e, condition.conditionNumber)}
            >
              <GripIndicator />
            </div>
          )}
          {isEditMode && !isScreenInUse && (
            <div id='commandpanel-conditioncard-editmode-addcondition-wrapper' title="Add Condition" style={{ cursor: 'pointer' }}>
              <AddCondition id='commandpanel-conditioncard-editmode-addcondition' onClick={addCondition} />
            </div>
          )}
          <div id={`commandpanel-conditioncard-editmode-conditionnum-${conditionNumberLable}`} className="ingredient-card-text"> {conditionNumberLable}</div>
          {isEditMode && isScreenEditable && (
            <div id='commandpanel-conditioncard-editmode-addingredient-wrapper' title="Add Ingredient" style={{ cursor: 'pointer' }}>
              <AddIngredient id='commandpanel-conditioncard-editmode-addingredient' onClick={addIngredient} />
            </div>
          )}
        </div>
        {isEditMode && !isScreenInUse && (
          <div
            id='commandpanel-conditioncard-editmode-copypaste-wrapper'
            className="flex flex-space-between flex-y-center conition-copy-paste"
            style={{ width: '31%' }}
          >
            <Copy
              id='commandpanel-conditioncard-editmode-copy'
              onClick={handleCopy}
              className={`cursor-pointer ${copyActive ? 'copy-condition-active' : ''}`}
              title="Copy"
            />
            <Paste id='commandpanel-conditioncard-editmode-paste' onClick={handlePaste} className="cursor-pointer" title="Paste" />
            <DelCond id='commandpanel-conditioncard-editmode-delete' onClick={handleDelete} className="cursor-pointer" title="Delete" />
          </div>
        )}
      </div>
      <div id='commandpanel-conditioncard-loop-container' className="flex flex-column flex-space-evenly w-100pr">
        {
          //        loop throug all ingredients of the condition and render them here.
          condition.conditionIngredient.map((ing) => {
            return (
              <IngredientCard
                id={`commandpanel-conditioncard-loop-ingcard-${ing.id}`}
                key={`ing-cardd-${ing.id}`}
                ingredientData={ing}
                customStyle={{
                  marginTop: '5px',
                  border: 'none',
                  borderTop: '1px solid #BFBFBF',
                  borderRadius: '0px',
                }}
                setContentView={setContentView}
                setEditIngredientData={setEditIngredientData}
                setSelectedIngredient={setSelectedIngredient}
                isEditMode={isEditMode}
                onDeleteIngredient={() => deleteConditionIngredient(ing.id)}
              />
            );
          })
        }
      </div>
    </div>
  );
}
